import { Form, Formik, type FormikProps } from "formik";
import { useRef, useState } from "react";
import Button from "../UI/Button";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import Editor, { type EditorHandle } from "../RichTextEditor/Editor";
import VariablePicker from "../RichTextEditor/VariablePicker";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { sanitizeValues, toInitialValuesFromSchema } from "../../models/utils";
import {
  MAIL_TEMPLATE_INITIAL_VALUES,
  mailTemplateSchema,
} from "../../models/mailTemplate";
import type { MailTemplateMeta } from "../../models/mailTemplate";
import useMailTemplate from "../../hooks/use-mail-template";
// @ts-expect-error pure js
import useLoader from "../../hooks/use-loader.js";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import styles from "./MailTemplateForm.module.css";

interface MailTemplateFormValues {
  subject: string;
  html: string;
}

interface MailTemplateFormProps {
  meta: MailTemplateMeta;
  profileBailleurId?: number;
  isProfile?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function MailTemplateForm({
  meta,
  profileBailleurId,
  isProfile,
  open,
  onOpenChange,
}: MailTemplateFormProps) {
  const { mailTemplate, createOrUpdateMailTemplate, resetMailTemplate } =
    useMailTemplate(meta.static_id, profileBailleurId);

  const [error, setError] = useState("");
  const subjectWrapperRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<EditorHandle>(null);

  async function submitHandler(values: MailTemplateFormValues) {
    setError("");
    try {
      await createOrUpdateMailTemplate(sanitizeValues(values));
      onOpenChange(false);
    } catch (e) {
      setError(handleAPIError(e));
    }
  }

  const [isResettingToDefault, resetToDefault] = useLoader(
    async (resetForm: () => void) => {
      try {
        const template = await resetMailTemplate(["subject", "html"]);
        resetForm();
        editorRef.current?.reset(template.html);
      } catch (e) {
        handleAPIError(e);
      }
    },
  );

  const [isSettingAccountTemplate, setAccountTemplate] = useLoader(
    async (resetForm: () => void) => {
      try {
        const template = await createOrUpdateMailTemplate({
          subject: null,
          html: null,
        });
        resetForm();
        editorRef.current?.reset(template.html);
      } catch (e) {
        handleAPIError(e);
      }
    },
  );

  const initialValues: MailTemplateFormValues = toInitialValuesFromSchema(
    mailTemplateSchema,
    { subject: mailTemplate?.subject, html: mailTemplate?.html },
    MAIL_TEMPLATE_INITIAL_VALUES,
  );

  function insertIntoSubject(
    key: string,
    setFieldValue: FormikProps<MailTemplateFormValues>["setFieldValue"],
  ) {
    const token = `{${key}}`;
    const input = subjectWrapperRef.current?.querySelector<HTMLInputElement>(
      'input[name="subject"]',
    );
    if (!input) return;
    const current = input.value;
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    setFieldValue("subject", next);
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + token.length;
      input.setSelectionRange(pos, pos);
    });
  }

  return (
    <Formik<MailTemplateFormValues>
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={mailTemplateSchema}
      onSubmit={submitHandler}
    >
      {({ setFieldValue, resetForm }) => (
        <ModalDialog
          size="lg"
          open={open}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
              editorRef.current?.reset();
              setError("");
            }
            onOpenChange(nextOpen);
          }}
          header={meta.title}
          subtitle={meta.description}
        >
          <Form>
            <ModalDialogBody className={styles.form}>
              <Text>
                Sélectionnez le sujet ou le corps du mail, et cliquez sur une
                variable disponible dans la liste pour l'insérer
              </Text>
              <VariablePicker
                available={meta.variables}
                label={"Variable(s) à insérer"}
                onInsert={(key) => {
                  if (editorRef.current?.isFocused()) {
                    editorRef.current.insertVariable(key);
                  } else {
                    insertIntoSubject(key, setFieldValue);
                  }
                }}
              />
              <div ref={subjectWrapperRef}>
                <TextInput
                  type={"text"}
                  name={"subject"}
                  label={"Sujet du mail"}
                  marginForm=""
                  mandatory
                />
              </div>
              <Editor
                key={mailTemplate?.html ?? ""}
                ref={editorRef}
                defaultValue={mailTemplate?.html ?? ""}
                onChange={(value) => setFieldValue("html", value)}
                availableVariables={meta.variables}
                showVariablePicker={false}
              />

              <Callout variant="danger">{error}</Callout>
            </ModalDialogBody>
            <ModalDialogFooter
              start={
                <>
                  <Button
                    variant="secondary"
                    onClick={() => resetToDefault(resetForm)}
                    isLoading={isResettingToDefault}
                  >
                    Utiliser le modèle par défaut
                  </Button>
                  {isProfile && (
                    <Button
                      variant="secondary"
                      onClick={() => setAccountTemplate(resetForm)}
                      isLoading={isSettingAccountTemplate}
                    >
                      Utiliser le modèle du compte
                    </Button>
                  )}
                </>
              }
            >
              <FormikSubmitButton>Valider</FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
